import React, { useCallback, useEffect, useMemo, useState } from "react";
import BranchSideBarItem from "./BranchSideBarItem";
import { Outlet, useNavigate } from "react-router-dom";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import NotificationsIcon from "@mui/icons-material/Notifications";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import CloseIcon from "@mui/icons-material/Close";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CircularProgress from "@mui/material/CircularProgress";
import baseUrl from "../../api/baseUrl";
import {
  getDecryptedToken,
  setEncryptedTokens,
} from "../../utils/crypto/cryptoUtils";
import brandLogo from "../../assets/logo.png";
import { getMediaUrl } from "../../utils/mediaUrl";

const martName = getDecryptedToken("martName");
const martLogoPath = getDecryptedToken("logo");
const userName = getDecryptedToken("name");
const designation = getDecryptedToken("designation");

function BranchLayout({ adminAccess }: any) {
  const [drawers, setDrawers] = useState([]);
  const [openDrawerModal, setOpenDrawerModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= 901,
  );
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= 901,
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [brandLogoFailed, setBrandLogoFailed] = useState(false);
  const [martLogoFailed, setMartLogoFailed] = useState(false);
  const navigate = useNavigate();
  const collapsed = isDesktop && !sidebarOpen;

  const martLogoUrl = useMemo(() => {
    if (!martLogoPath || martLogoPath === "null" || martLogoPath === "") {
      return null;
    }
    return getMediaUrl(martLogoPath);
  }, []);

  const showMartLogo = Boolean(martLogoUrl) && !martLogoFailed;
  const martInitials = (martName || "BR")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part: string) => part[0]?.toUpperCase())
    .join("");

  const fetchDrawers = useCallback(() => {
    setLoading(true);
    baseUrl
      .get("/branch/drawer/get-drawers")
      .then((res) => setDrawers(res.data.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const goToPos = () => {
    fetchDrawers();
    setOpenDrawerModal(true);
  };

  const handleDrawerSelect = (drawer: any) => {
    setEncryptedTokens([{ key: "selectedDrawer", value: `${drawer?.id}` }]);
    setOpenDrawerModal(false);
    navigate("/pos");
  };

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 901);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpenDrawerModal(false);
        setMenuOpen(false);
        if (!isDesktop) setSidebarOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isDesktop]);

  return (
    <div className="flex h-full min-h-screen w-full bg-erp-surface font-inter text-erp-ink">
      {sidebarOpen ? (
        <button
          type="button"
          aria-label="Close sidebar overlay"
          className="fixed inset-0 z-40 border-0 bg-[#06101a]/55 backdrop-blur-[2px] min-[901px]:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      ) : null}

      <aside
        className={[
          "z-50 flex h-full shrink-0 flex-col overflow-hidden text-white transition-[width,transform] duration-300 ease-out",
          "bg-[linear-gradient(180deg,#0b1c2c_0%,#123048_55%,#0f2a40_100%)]",
          "max-[900px]:fixed max-[900px]:inset-y-0 max-[900px]:left-0 max-[900px]:w-[220px]",
          sidebarOpen
            ? "w-[220px] max-[900px]:translate-x-0"
            : "w-16 max-[900px]:w-[220px] max-[900px]:-translate-x-full",
        ].join(" ")}
      >
        <div
          className={[
            "flex h-full flex-col",
            collapsed ? "w-16" : "w-[220px]",
          ].join(" ")}
        >
          <div
            className={[
              "relative border-b border-white/10 pt-5",
              collapsed ? "px-2 pb-3" : "px-5 pb-4",
            ].join(" ")}
          >
            <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-[radial-gradient(circle_at_20%_0%,rgba(201,162,77,0.22),transparent_55%)]" />
            <div className="relative flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div
                  className={[
                    "flex items-center",
                    collapsed ? "justify-center" : "gap-3",
                  ].join(" ")}
                >
                  <div
                    className={[
                      "flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-black shadow-[0_0_0_2px_rgba(201,162,77,0.35)]",
                      collapsed ? "h-10 w-10" : "h-11 w-11",
                    ].join(" ")}
                  >
                    {!brandLogoFailed ? (
                      <img
                        src={brandLogo}
                        alt="Cholasys"
                        className={[
                          "scale-[1.35] object-cover object-left",
                          collapsed ? "h-10 w-10" : "h-11 w-11",
                        ].join(" ")}
                        onError={() => setBrandLogoFailed(true)}
                      />
                    ) : (
                      <span className="font-brand text-base text-erp-gold">C</span>
                    )}
                  </div>
                  {!collapsed ? (
                    <div className="min-w-0">
                      <p className="m-0 truncate text-[1.15rem] font-bold leading-none tracking-[-0.02em] text-white">
                        Cholasys
                      </p>
                      <p className="mt-1 text-[0.62rem] font-semibold uppercase tracking-[0.16em] text-erp-gold">
                        Enterprise ERP
                      </p>
                    </div>
                  ) : null}
                </div>
              </div>
              {!collapsed ? (
                <button
                  type="button"
                  className="inline-flex border-0 bg-transparent p-1 text-white/70 hover:bg-transparent hover:text-white min-[901px]:hidden"
                  onClick={() => setSidebarOpen(false)}
                  aria-label="Close menu"
                >
                  <CloseIcon fontSize="small" />
                </button>
              ) : null}
            </div>
          </div>

          <nav className="min-h-0 flex-1 overflow-y-auto px-0.5 py-3">
            {!collapsed ? (
              <p className="mb-2 px-4 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#6f879c]">
                Navigation
              </p>
            ) : null}
            <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
              {adminAccess?.map((item: any, i: number) => (
                <BranchSideBarItem
                  key={i}
                  item={item}
                  collapsed={collapsed}
                  onNavigate={() => {
                    if (!isDesktop) setSidebarOpen(false);
                  }}
                />
              ))}
            </ul>
          </nav>

          {!collapsed ? (
            <div className="border-t border-white/10 px-5 py-3">
              <p className="m-0 text-[12px] text-[#7f96ad]">
                Powered by{" "}
                <span className="font-semibold text-erp-gold">VisoLabs</span>
              </p>
            </div>
          ) : null}
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-[68px] shrink-0 items-center justify-between border-b border-erp-line bg-erp-card px-4 shadow-[0_1px_0_rgba(11,28,44,0.04)] min-[901px]:px-7">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              className="inline-flex rounded-md border border-erp-line bg-erp-surface p-1.5 text-erp-ink transition-colors hover:border-erp-accent hover:bg-white hover:text-erp-accent"
              onClick={() => setSidebarOpen((prev) => !prev)}
              aria-label="Toggle sidebar"
            >
              <MenuOpenIcon />
            </button>

            <div className="flex min-w-0 items-center gap-3 rounded-md border border-erp-line bg-erp-surface px-2.5 py-1.5">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-md bg-erp-navy text-[0.72rem] font-bold text-erp-gold">
                {showMartLogo ? (
                  <img
                    src={martLogoUrl as string}
                    alt={martName || "Branch logo"}
                    className="h-full w-full object-contain p-1"
                    onError={() => setMartLogoFailed(true)}
                  />
                ) : (
                  <span>{martInitials || "BR"}</span>
                )}
              </div>
              <div className="min-w-0">
                <p className="m-0 truncate text-[0.9rem] font-semibold text-erp-ink">
                  {martName || "Branch workspace"}
                </p>
                <p className="m-0 truncate text-[0.7rem] text-erp-muted">
                  Active branch
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2.5 min-[901px]:gap-3">
            <button
              type="button"
              onClick={goToPos}
              className="inline-flex items-center gap-2 rounded-md border-0 bg-gradient-to-r from-erp-gold to-[#b8923d] px-3.5 py-2 text-[0.78rem] font-bold uppercase tracking-[0.08em] text-erp-navy shadow-[0_8px_18px_rgba(201,162,77,0.35)] transition-[filter,transform] duration-150 hover:brightness-105 hover:-translate-y-px"
              title="Open POS"
            >
              <PointOfSaleIcon fontSize="small" />
              <span className="max-[640px]:hidden">Open POS</span>
            </button>

            <button
              type="button"
              className="inline-flex rounded-md border border-erp-line bg-white p-2 text-erp-muted transition-colors hover:border-erp-accent hover:text-erp-accent"
              aria-label="Notifications"
            >
              <NotificationsIcon fontSize="small" />
            </button>

            <div className="relative">
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-md border border-erp-line bg-white py-1.5 pl-1.5 pr-2.5 text-erp-ink transition-colors hover:border-erp-accent"
                onClick={() => setMenuOpen((prev) => !prev)}
                aria-label="Account menu"
              >
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-erp-navy text-erp-gold">
                  <AccountCircleIcon fontSize="small" />
                </span>
                <span className="hidden min-w-0 text-left sm:block">
                  <span className="block max-w-[120px] truncate text-[0.78rem] font-semibold leading-tight">
                    {userName || "User"}
                  </span>
                  <span className="block max-w-[120px] truncate text-[0.68rem] text-erp-muted leading-tight">
                    {designation || "Staff"}
                  </span>
                </span>
              </button>
              {menuOpen ? (
                <div className="absolute right-0 top-[calc(100%+8px)] z-30 min-w-[160px] rounded-md border border-erp-line bg-white p-2 shadow-[0_16px_40px_rgba(11,28,44,0.16)]">
                  <button
                    type="button"
                    className="w-full rounded-md border-0 bg-transparent px-3 py-2 text-left text-[0.86rem] font-medium text-erp-ink hover:bg-erp-surface hover:text-erp-accent"
                    onClick={() => {
                      localStorage.clear();
                      window.location.href = "/login";
                    }}
                  >
                    Logout
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </header>

        <main className="min-h-0 flex-1 overflow-y-scroll bg-[#eef3f8] p-3 min-[901px]:p-4 [scrollbar-gutter:stable]">
          <Outlet />
        </main>
      </div>

      {openDrawerModal ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-[#06101a]/55 p-4 backdrop-blur-[2px]">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="drawer-dialog-title"
            className="w-full max-w-md overflow-hidden rounded-md border border-white/50 bg-white shadow-[0_28px_70px_rgba(6,16,26,0.35)]"
          >
            <div className="flex items-center justify-between border-b border-erp-line bg-[linear-gradient(90deg,#0b1c2c,#123048)] px-5 py-4 text-white">
              <div>
                <h2
                  id="drawer-dialog-title"
                  className="m-0 text-[1.05rem] font-semibold tracking-[-0.02em]"
                >
                  Select a drawer
                </h2>
                <p className="m-0 mt-1 text-[0.75rem] text-[#a9bdd0]">
                  Choose a cash drawer to open POS
                </p>
              </div>
              <button
                type="button"
                className="inline-flex border-0 bg-transparent p-1 text-white/70 hover:bg-transparent hover:text-white"
                onClick={() => setOpenDrawerModal(false)}
                aria-label="Close"
              >
                <CloseIcon fontSize="small" />
              </button>
            </div>

            <div className="max-h-[50vh] overflow-y-auto p-2">
              {loading ? (
                <div className="flex justify-center py-10">
                  <CircularProgress size={28} sx={{ color: "#1f6f8b" }} />
                </div>
              ) : drawers.length > 0 ? (
                <ul className="m-0 flex list-none flex-col gap-1 p-0">
                  {drawers.map((drawer: any) => (
                    <li key={drawer.id}>
                      <button
                        type="button"
                        onClick={() => handleDrawerSelect(drawer)}
                        className="flex w-full items-center gap-3 rounded-md border-0 bg-transparent px-3 py-3 text-left transition-colors hover:bg-erp-surface"
                      >
                        <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-erp-accent/12 text-erp-accent">
                          <AccountBalanceIcon fontSize="small" />
                        </span>
                        <span className="text-[0.95rem] font-semibold text-erp-ink">
                          {drawer.name}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="px-3 py-8 text-center text-[0.92rem] text-erp-muted">
                  No drawers available
                </p>
              )}
            </div>

            <div className="flex justify-end border-t border-erp-line px-4 py-3">
              <button
                type="button"
                onClick={() => setOpenDrawerModal(false)}
                className="rounded-md border border-erp-line bg-white px-4 py-2 text-[0.86rem] font-semibold text-erp-ink hover:border-erp-accent hover:text-erp-accent"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default BranchLayout;
